import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Inventory service's public contract.
 *
 * `quantityOnHand` never appears as a writable field on the item schemas —
 * only `InventoryMovement` changes it, so there is no request shape here that
 * would let a caller bypass the ledger. A checkout references a person by id
 * and nothing more, resolved through People's read port, the same discipline
 * `groups.contract.ts` documents for a roster entry.
 */

export const INVENTORY_ITEM_TYPES = ['ASSET', 'CONSUMABLE'] as const;
export type InventoryItemType = (typeof INVENTORY_ITEM_TYPES)[number];

export const ITEM_CONDITIONS = [
  'NEW',
  'GOOD',
  'FAIR',
  'POOR',
  'DAMAGED',
  'OUT_OF_SERVICE',
] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];

export const INVENTORY_MOVEMENT_TYPES = [
  'RECEIPT',
  'CONSUMPTION',
  'TRANSFER',
  'ADJUSTMENT',
  'DISPOSAL',
] as const;
export type InventoryMovementType = (typeof INVENTORY_MOVEMENT_TYPES)[number];

export const INVENTORY_CHECKOUT_STATUSES = ['OUT', 'RETURNED', 'LOST'] as const;
export type InventoryCheckoutStatus = (typeof INVENTORY_CHECKOUT_STATUSES)[number];

export const MAINTENANCE_TYPES = ['SCHEDULED', 'REPAIR'] as const;
export type MaintenanceType = (typeof MAINTENANCE_TYPES)[number];

const nameField = z.string().trim().min(1).max(150);
const positiveQuantityField = z.number().positive();

/** Shared by every archive action in this contract — Category, Location,
 *  Supplier and Item all archive the same way, so one schema serves all four
 *  rather than four copies of the same object. */
export const archiveInventoryRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveInventoryRequest = z.infer<typeof archiveInventoryRequestSchema>;

// --- Categories --------------------------------------------------------------

export const createInventoryCategoryRequestSchema = z.object({
  name: nameField,
  description: z.string().trim().max(2000).nullish(),
  parentCategoryId: z.uuid().nullish(),
});
export type CreateInventoryCategoryRequest = z.infer<typeof createInventoryCategoryRequestSchema>;

export const updateInventoryCategoryRequestSchema = z
  .object({
    name: nameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    parentCategoryId: z.uuid().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateInventoryCategoryRequest = z.infer<typeof updateInventoryCategoryRequestSchema>;

export const listInventoryCategoriesQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  parentCategoryId: z.uuid().nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListInventoryCategoriesQuery = z.infer<typeof listInventoryCategoriesQuerySchema>;

export interface InventoryCategorySummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly parentCategoryId: string | null;
  readonly itemCount: number;
  readonly archivedAt: string | null;
}

export interface InventoryCategoryDetailResponse extends InventoryCategorySummaryResponse {
  readonly description: string | null;
  readonly parentCategoryName: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// --- Locations -----------------------------------------------------------------

export const createInventoryLocationRequestSchema = z.object({
  name: nameField,
  description: z.string().trim().max(2000).nullish(),
  campusId: z.uuid().nullish(),
});
export type CreateInventoryLocationRequest = z.infer<typeof createInventoryLocationRequestSchema>;

export const updateInventoryLocationRequestSchema = z
  .object({
    name: nameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    campusId: z.uuid().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateInventoryLocationRequest = z.infer<typeof updateInventoryLocationRequestSchema>;

export const listInventoryLocationsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  campusId: z.uuid().nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListInventoryLocationsQuery = z.infer<typeof listInventoryLocationsQuerySchema>;

export interface InventoryLocationSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly campusId: string | null;
  readonly itemCount: number;
  readonly archivedAt: string | null;
}

export interface InventoryLocationDetailResponse extends InventoryLocationSummaryResponse {
  readonly description: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// --- Suppliers -----------------------------------------------------------------

export const createInventorySupplierRequestSchema = z.object({
  name: nameField,
  contactName: z.string().trim().max(150).nullish(),
  phone: z.string().trim().max(30).nullish(),
  email: z.email().nullish(),
  address: z.string().trim().max(500).nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type CreateInventorySupplierRequest = z.infer<typeof createInventorySupplierRequestSchema>;

export const updateInventorySupplierRequestSchema = z
  .object({
    name: nameField.optional(),
    contactName: z.string().trim().max(150).nullish(),
    phone: z.string().trim().max(30).nullish(),
    email: z.email().nullish(),
    address: z.string().trim().max(500).nullish(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateInventorySupplierRequest = z.infer<typeof updateInventorySupplierRequestSchema>;

export const listInventorySuppliersQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListInventorySuppliersQuery = z.infer<typeof listInventorySuppliersQuerySchema>;

export interface InventorySupplierSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly archivedAt: string | null;
}

export interface InventorySupplierDetailResponse extends InventorySupplierSummaryResponse {
  readonly contactName: string | null;
  readonly address: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// --- Items -----------------------------------------------------------------

export const createInventoryItemRequestSchema = z.object({
  categoryId: z.uuid(),
  locationId: z.uuid(),
  supplierId: z.uuid().nullish(),
  name: nameField,
  description: z.string().trim().max(2000).nullish(),
  type: z.enum(INVENTORY_ITEM_TYPES).default('CONSUMABLE'),
  sku: z.string().trim().max(60).nullish(),
  serialNumber: z.string().trim().max(120).nullish(),
  condition: z.enum(ITEM_CONDITIONS).default('GOOD'),
  /** Recorded as an opening RECEIPT movement, never written to
   *  `quantityOnHand` directly. Zero, the default, needs no movement. */
  initialQuantity: z.number().min(0).default(0),
  unit: z.string().trim().min(1).max(30).default('unidad'),
  minQuantity: z.number().min(0).nullish(),
  acquisitionDate: z.iso.date().nullish(),
  acquisitionCost: z.number().min(0).nullish(),
  currentValue: z.number().min(0).nullish(),
  photoFileId: z.uuid().nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type CreateInventoryItemRequest = z.infer<typeof createInventoryItemRequestSchema>;

/**
 * Declared field by field rather than `.partial()` — the same reason
 * `updateGroupRequestSchema` gives: `.partial()` keeps `.default()` values, so
 * an empty PATCH would overwrite real data with defaults.
 *
 * `quantityOnHand` is absent on purpose. Stock only changes through a
 * movement (see below).
 */
export const updateInventoryItemRequestSchema = z
  .object({
    categoryId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    supplierId: z.uuid().nullish(),
    name: nameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    type: z.enum(INVENTORY_ITEM_TYPES).optional(),
    sku: z.string().trim().max(60).nullish(),
    serialNumber: z.string().trim().max(120).nullish(),
    condition: z.enum(ITEM_CONDITIONS).optional(),
    unit: z.string().trim().min(1).max(30).optional(),
    minQuantity: z.number().min(0).nullish(),
    acquisitionDate: z.iso.date().nullish(),
    acquisitionCost: z.number().min(0).nullish(),
    currentValue: z.number().min(0).nullish(),
    photoFileId: z.uuid().nullish(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateInventoryItemRequest = z.infer<typeof updateInventoryItemRequestSchema>;

export const listInventoryItemsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  categoryId: z.uuid().nullish(),
  locationId: z.uuid().nullish(),
  supplierId: z.uuid().nullish(),
  type: z.enum(INVENTORY_ITEM_TYPES).nullish(),
  /** Items whose `quantityOnHand` is at or below `minQuantity`. Computed at
   *  read time — there is no stored "low stock" flag to drift out of date. */
  lowStockOnly: queryBoolean().default(false),
  includeArchived: queryBoolean().default(false),
});
export type ListInventoryItemsQuery = z.infer<typeof listInventoryItemsQuerySchema>;

export interface InventoryItemSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly type: InventoryItemType;
  readonly categoryId: string;
  readonly categoryName: string;
  readonly locationId: string;
  readonly locationName: string;
  readonly condition: ItemCondition;
  readonly quantityOnHand: number;
  readonly unit: string;
  readonly minQuantity: number | null;
  readonly isLowStock: boolean;
  readonly archivedAt: string | null;
}

export interface InventoryItemDetailResponse extends InventoryItemSummaryResponse {
  readonly description: string | null;
  readonly sku: string | null;
  readonly serialNumber: string | null;
  readonly supplierId: string | null;
  readonly supplierName: string | null;
  readonly acquisitionDate: string | null;
  readonly acquisitionCost: number | null;
  readonly currentValue: number | null;
  readonly photoFileId: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// --- Movements ---------------------------------------------------------------

/**
 * `quantity` is positive for every type except ADJUSTMENT — a RECEIPT,
 * CONSUMPTION, DISPOSAL or TRANSFER is always a magnitude, with direction
 * coming from `type`, so a form never has to explain to a volunteer why
 * consuming ten candles means typing "-10". A correction found by a physical
 * count can go either way, so ADJUSTMENT alone may be negative.
 * `ItemsService` turns this into the signed delta the ledger and the
 * running balance actually use.
 */
export const createInventoryMovementRequestSchema = z
  .object({
    type: z.enum(INVENTORY_MOVEMENT_TYPES),
    quantity: z
      .number()
      .finite()
      .refine((n) => n !== 0, 'Quantity must not be zero'),
    fromLocationId: z.uuid().nullish(),
    toLocationId: z.uuid().nullish(),
    reason: z.string().trim().max(500).nullish(),
    occurredAt: z.iso.datetime().nullish(),
  })
  .refine((value) => value.type === 'ADJUSTMENT' || value.quantity > 0, {
    message: 'Quantity must be positive for this movement type',
    path: ['quantity'],
  })
  .refine((value) => value.type !== 'TRANSFER' || (value.fromLocationId && value.toLocationId), {
    message: 'A transfer needs both a source and a destination location',
    path: ['toLocationId'],
  })
  .refine((value) => value.type !== 'TRANSFER' || value.fromLocationId !== value.toLocationId, {
    message: 'Source and destination must differ',
    path: ['toLocationId'],
  });
export type CreateInventoryMovementRequest = z.infer<typeof createInventoryMovementRequestSchema>;

export const listInventoryMovementsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  itemId: z.uuid().nullish(),
  type: z.enum(INVENTORY_MOVEMENT_TYPES).nullish(),
});
export type ListInventoryMovementsQuery = z.infer<typeof listInventoryMovementsQuerySchema>;

export interface InventoryMovementResponse {
  readonly id: string;
  readonly itemId: string;
  readonly itemName: string;
  readonly type: InventoryMovementType;
  readonly quantity: number;
  readonly fromLocationId: string | null;
  readonly fromLocationName: string | null;
  readonly toLocationId: string | null;
  readonly toLocationName: string | null;
  readonly reason: string | null;
  readonly performedByUserId: string | null;
  readonly occurredAt: string;
}

// --- Checkouts -----------------------------------------------------------------

export const createInventoryCheckoutRequestSchema = z.object({
  borrowerPersonId: z.uuid(),
  quantity: positiveQuantityField.default(1),
  eventId: z.uuid().nullish(),
  dueAt: z.iso.datetime().nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type CreateInventoryCheckoutRequest = z.infer<typeof createInventoryCheckoutRequestSchema>;

export const returnInventoryCheckoutRequestSchema = z.object({
  returnCondition: z.enum(ITEM_CONDITIONS),
  notes: z.string().trim().max(2000).nullish(),
});
export type ReturnInventoryCheckoutRequest = z.infer<typeof returnInventoryCheckoutRequestSchema>;

export const reportLostInventoryCheckoutRequestSchema = z.object({
  notes: z.string().trim().max(2000).nullish(),
});
export type ReportLostInventoryCheckoutRequest = z.infer<
  typeof reportLostInventoryCheckoutRequestSchema
>;

export const listInventoryCheckoutsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  itemId: z.uuid().nullish(),
  borrowerPersonId: z.uuid().nullish(),
  status: z.enum(INVENTORY_CHECKOUT_STATUSES).nullish(),
  overdueOnly: queryBoolean().default(false),
});
export type ListInventoryCheckoutsQuery = z.infer<typeof listInventoryCheckoutsQuerySchema>;

export interface InventoryCheckoutResponse {
  readonly id: string;
  readonly itemId: string;
  readonly itemName: string;
  readonly quantity: number;
  readonly borrowerPersonId: string;
  readonly borrowerDisplayName: string;
  readonly eventId: string | null;
  readonly status: InventoryCheckoutStatus;
  readonly checkedOutAt: string;
  readonly dueAt: string | null;
  readonly returnedAt: string | null;
  readonly returnCondition: ItemCondition | null;
  readonly notes: string | null;
  /** `status === 'OUT'` and `dueAt` has passed. Computed at read time. */
  readonly isOverdue: boolean;
}

// --- Maintenance ---------------------------------------------------------------

export const createMaintenanceRecordRequestSchema = z.object({
  type: z.enum(MAINTENANCE_TYPES),
  scheduledDate: z.iso.date().nullish(),
  completedDate: z.iso.date().nullish(),
  cost: z.number().min(0).nullish(),
  performedBy: z.string().trim().max(150).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  nextDueDate: z.iso.date().nullish(),
});
export type CreateMaintenanceRecordRequest = z.infer<typeof createMaintenanceRecordRequestSchema>;

export const updateMaintenanceRecordRequestSchema = z
  .object({
    scheduledDate: z.iso.date().nullish(),
    completedDate: z.iso.date().nullish(),
    cost: z.number().min(0).nullish(),
    performedBy: z.string().trim().max(150).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    nextDueDate: z.iso.date().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateMaintenanceRecordRequest = z.infer<typeof updateMaintenanceRecordRequestSchema>;

export const listMaintenanceRecordsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  itemId: z.uuid().nullish(),
  type: z.enum(MAINTENANCE_TYPES).nullish(),
  /** Records whose `nextDueDate` has passed and have no `completedDate`. */
  dueOnly: queryBoolean().default(false),
});
export type ListMaintenanceRecordsQuery = z.infer<typeof listMaintenanceRecordsQuerySchema>;

export interface MaintenanceRecordResponse {
  readonly id: string;
  readonly itemId: string;
  readonly itemName: string;
  readonly type: MaintenanceType;
  readonly scheduledDate: string | null;
  readonly completedDate: string | null;
  readonly cost: number | null;
  readonly performedBy: string | null;
  readonly notes: string | null;
  readonly nextDueDate: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// --- Dashboard alerts ------------------------------------------------------

/** Backs the dashboard's low-stock / maintenance-due widget — one cheap,
 *  read-only query, no stored alert state to fall out of sync. */
export interface InventoryAlertsResponse {
  readonly lowStockItems: readonly InventoryItemSummaryResponse[];
  readonly overdueCheckouts: readonly InventoryCheckoutResponse[];
  readonly dueMaintenance: readonly MaintenanceRecordResponse[];
}
